// src/App.js
import { useEffect, useState, useMemo, useCallback } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";

import { useTranslation } from "react-i18next";

import Landing from "./pages/LandingPage/LandingPage";
import AuthPage from "./pages/AuthPage";
import WorkoutPage from "./pages/WorkoutPage";
import ProfilePage from "./pages/ProfilePage/ProfilePage";
import HomePage from "./pages/HomePage";
import AnalyticsPage from "./pages/AnalyticsPage";
import PlanPage from "./pages/PlanPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsPage from "./pages/TermsPage";
import ResetPasswordPage from "./pages/PasswordResetPage";
import AboutUsPage from "./pages/LandingPage/AboutUsPage";
import Onboarding from "./components/Onboarding";
import BottomNav from "./components/BottomNav";
import PlanDayDetailPage from "./pages/PlanDayDetailPage";
import OnboardingPlanPage from "./pages/OnboardingPlanPage";
import OptionsPage from "./pages/OptionsPage";
import PlanDetailPage from "./pages/PlanDetailPage";

import RouteBodyClass from "./components/RouteBodyClass";

import { api } from "./api";
import appWallpaper from "./assets/olympeak_visual_4.png";

/* -------------------------------------------------------------------------- */
/* Assets helpers                                                             */
/* -------------------------------------------------------------------------- */

function loadWallpapersMap() {
  // CRA/Webpack: loads from src/assets/wallpapers
  const req = require.context(
    "./assets/wallpapers",
    false,
    /\.(png|jpe?g|webp|svg)$/i
  );
  const map = new Map();

  req.keys().forEach((p) => {
    const mod = req(p);
    const src = mod?.default ?? mod;
    const filename = p.replace("./", "");
    const key = filename.replace(/\.[^.]+$/, "");
    map.set(key, src);
  });

  return map;
}

const WALLPAPERS = loadWallpapersMap();

/* -------------------------------------------------------------------------- */
/* Onboarding skip helper                                                     */
/* -------------------------------------------------------------------------- */

function skipKeyFor(user) {
  const id = user?.id || user?.email || "anon";
  return `plan_onboarding_skipped_v1:${id}`;
}

/* -------------------------------------------------------------------------- */
/* Route body class layout                                                    */
/* -------------------------------------------------------------------------- */

function AppLayout({ isAuthed }) {
  return (
    <>
      <RouteBodyClass isAuthed={isAuthed} />
      <Outlet />
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Gate                                                                       */
/* -------------------------------------------------------------------------- */

function AppGate({ user, booting, afterAuth, onSkipPlanOnboarding }) {
  const location = useLocation();

  if (booting) return null;

  if (!user) {
    if (
      location.pathname === "/auth" ||
      location.pathname === "/privacy" ||
      location.pathname === "/about" ||
      location.pathname === "/reset-password"
    ) {
      return <Outlet />;
    }

    return (
      <Landing
        onDevLogin={async (email) => {
          await api(`/dev/login?email=${encodeURIComponent(email)}`, {
            method: "POST",
          });
          await afterAuth();
        }}
      />
    );
  }

  // Gate: onboarding required only if not onboarded
  if (!user.onboarded) {
    return <Onboarding onComplete={afterAuth} onSkip={onSkipPlanOnboarding} />;
  }

  const hideBottomNav = location.pathname === "/onboarding";

  return (
    <>
      <Outlet />
      {!hideBottomNav && <BottomNav />}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* App                                                                        */
/* -------------------------------------------------------------------------- */

export default function App() {
  const { i18n } = useTranslation();

  const [user, setUser] = useState(null);
  const [booting, setBooting] = useState(true);

  const loadMe = useCallback(async () => {
    try {
      const { user: me } = await api("/auth/me", { cache: "no-store" });
      if (!me) {
        setUser(null);
        return;
      }

      const profileRes = await api("/me/profile").catch(() => ({}));
      const profile = profileRes?.profile ?? null;

      const prefsRes = await api("/me/preferences").catch(() => null);
      const hasPlanPrefs = Boolean(prefsRes?.goal);

      const skipped = localStorage.getItem(skipKeyFor(me)) === "1";

      setUser({
        ...me,
        profile,
        hasPlanPrefs,
        planOnboardingSkipped: skipped,
        onboarded: hasPlanPrefs || skipped,
      });
    } finally {
      setBooting(false);
    }
  }, []);

  const afterAuth = useCallback(async () => {
    api.clearCache();
    setBooting(true);
    await loadMe();
  }, [loadMe]);

  const handleSkipPlanOnboarding = useCallback(() => {
    if (!user) return;

    localStorage.setItem(skipKeyFor(user), "1");

    setUser((prev) =>
      prev
        ? {
            ...prev,
            planOnboardingSkipped: true,
            onboarded: true,
          }
        : prev
    );
  }, [user]);

  const handleLogout = useCallback(async () => {
    try {
      await api("/auth/logout", { method: "POST" });
    } catch {}

    api.clearCache();
    setUser(null);
    localStorage.removeItem("app_user_v1");

    // optional: reset to default wallpaper on logout
    document.documentElement.style.setProperty(
      "--app-wallpaper",
      `url(${appWallpaper})`
    );
    document.documentElement.style.setProperty("--app-wallpaper-pos", "50% 50%");

    // optional: reset language to default on logout
    try {
      const next = "en";
      if (i18n.language !== next) i18n.changeLanguage(next);
    } catch {}
  }, [i18n]);

  // boot
  useEffect(() => {
    loadMe();
  }, [loadMe]);

  // ✅ GLOBAL language: DB when logged in, localStorage when logged out
  useEffect(() => {
    const normalizeLang = (v) => {
      const s = String(v || "").trim().toLowerCase();
      return s === "sk" ? "sk" : "en";
    };

    const getPublicLangFallback = () => {
      try {
        const stored = localStorage.getItem("app_lang_v1");
        if (stored) return normalizeLang(stored);
      } catch {}
      return null;
    };

    const fromProfile = user?.profile?.language
      ? normalizeLang(user.profile.language)
      : null;

    const fromPublic = !user ? getPublicLangFallback() : null;

    const lang = fromProfile || fromPublic || "en";

    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [user?.profile?.language, user, i18n]);

  // ✅ GLOBAL wallpaper: reacts to DB value (no page should override/restore this)
  useEffect(() => {
    const key = user?.profile?.wallpaperKey || "";
    const srcFromKey = key ? WALLPAPERS.get(key) : null;
    const finalSrc = srcFromKey || appWallpaper;

    document.documentElement.style.setProperty(
      "--app-wallpaper",
      `url(${finalSrc})`
    );
    document.documentElement.style.setProperty("--app-wallpaper-pos", "50% 50%");
  }, [user?.profile?.wallpaperKey]);

  // persist user snapshot (optional)
  useEffect(() => {
    if (user) localStorage.setItem("app_user_v1", JSON.stringify(user));
    else localStorage.removeItem("app_user_v1");
  }, [user]);

  const router = useMemo(
    () =>
      createBrowserRouter([
        {
          element: <AppLayout isAuthed={!!user} />,
          children: [
            // Public standalone pages
            { path: "/privacy", element: <PrivacyPolicy /> },
            { path: "/reset-password", element: <ResetPasswordPage /> },
            { path: "/terms", element: <TermsPage /> },
            { path: "/about", element: <AboutUsPage /> },
            {
              path: "/auth",
              element: user ? (
                <Navigate to="/" replace />
              ) : (
                <AuthPage onSuccess={afterAuth} />
              ),
            },

            // Everything else goes through the gate
            {
              element: (
                <AppGate
                  user={user}
                  booting={booting}
                  afterAuth={afterAuth}
                  onSkipPlanOnboarding={handleSkipPlanOnboarding}
                />
              ),
              children: [
                { path: "/", element: <HomePage user={user} onLogout={handleLogout} /> },
                { path: "/workout", element: <WorkoutPage user={user} onLogout={handleLogout} /> },
                { path: "/plan", element: <PlanPage user={user} onLogout={handleLogout} /> },
                { path: "/plan/:planId", element: <PlanDetailPage user={user} onLogout={handleLogout} /> },
                { path: "/plan/day/:ymd", element: <PlanDayDetailPage user={user} onLogout={handleLogout} /> },
                { path: "/onboarding", element: <OnboardingPlanPage user={user} onLogout={handleLogout} /> },
                { path: "/options", element: <OptionsPage user={user} onLogout={handleLogout} /> },
                { path: "/analytics", element: <AnalyticsPage user={user} onLogout={handleLogout} /> },
                { path: "/profile", element: <ProfilePage user={user} onLogout={handleLogout} /> },

                { path: "*", element: <Navigate to="/" replace /> },
              ],
            },
          ],
        },
      ]),
    [user, booting, afterAuth, handleSkipPlanOnboarding, handleLogout]
  );

  return <RouterProvider router={router} />;
}